export type ServiceFeature = { id?: string; title: string; text: string };

export type ServiceDetail = {
  slug: string;
  name: string;
  category: "Websites" | "Growth" | "Ongoing";
  headline: string;
  intro: string;
  included: string[];
  whoFor: string[];
  pageTypes: string[];
  features: ServiceFeature[];
  faqs: { q: string; a: string }[];
};

const BUILD_FAQ = {
  q: "How much does it cost?",
  a: "Website Development starts from the $600 one-time build, with Hosting & Maintenance at $75/month. Your exact scope is confirmed in writing before work begins.",
};

export const SERVICES: ServiceDetail[] = [
  {
    slug: "small-business",
    name: "Small Business Websites",
    category: "Websites",
    headline: "A website that makes your business look as good as it is.",
    intro: "A clean, professional site that explains what you do, shows why customers should trust you, and makes it easy to call or get in touch.",
    included: ["Custom homepage design", "Service and about pages", "Contact form and click-to-call", "Google Maps and hours", "Basic on-page SEO", "Mobile-first build"],
    whoFor: ["Local service businesses", "Owners with an outdated site", "Businesses with no website yet", "Teams relying only on social media"],
    pageTypes: ["Home", "Services", "About", "Contact", "Reviews", "FAQ"],
    features: [
      { title: "Clear calls to action", text: "Call, book or request a quote from every page." },
      { title: "Local trust signals", text: "Areas served, hours and real photos front and center." },
      { title: "Fast on phones", text: "Lightweight pages built for mobile visitors first." },
      { title: "Easy to update", text: "Request changes through your client dashboard." },
    ],
    faqs: [BUILD_FAQ, { q: "I don't have a logo. Can you still build my site?", a: "Yes. We can work with a simple wordmark and your brand colors, and swap in a logo later." }],
  },
  {
    slug: "restaurant",
    name: "Restaurant Websites",
    category: "Websites",
    headline: "Menus, hours and reservations — right where hungry people look.",
    intro: "A restaurant site built for phones: a readable menu, accurate hours, one-tap reservations and links to your delivery partners.",
    included: ["Mobile-friendly menu pages", "Hours and location with maps", "Reservation button or booking link", "Delivery partner links", "Photo gallery", "Events or specials section"],
    whoFor: ["Restaurants and cafés", "Food trucks", "Bars and lounges", "Bakeries and catering"],
    pageTypes: ["Home", "Menu", "Reservations", "Location & Hours", "Gallery", "Private Events"],
    features: [
      { title: "Menu", text: "A real, searchable menu instead of a PDF — easy to update." },
      { title: "Hours", text: "Clear opening hours, holiday notes and directions." },
      { title: "Reservations", text: "A booking button linked to your reservation tool or phone." },
      { title: "Delivery links", text: "Direct buttons to the delivery platforms you use." },
    ],
    faqs: [BUILD_FAQ, { q: "Can I update my menu myself?", a: "Send menu changes through your client dashboard and we'll update them as part of Hosting & Maintenance." }],
  },
  {
    slug: "automotive",
    name: "Automotive Websites",
    category: "Websites",
    headline: "Turn searches into booked service appointments.",
    intro: "For repair shops, detailers and dealers: a clear service list, online booking and the trust signals drivers look for before they call.",
    included: ["Service list with descriptions", "Online booking or request form", "Trust badges and certifications", "Click-to-call header", "Location and hours", "Photo gallery"],
    whoFor: ["Auto repair shops", "Detailing and tint studios", "Tire and body shops", "Independent dealers"],
    pageTypes: ["Home", "Services", "Book Service", "About", "Gallery", "Contact"],
    features: [
      { title: "Booking", text: "Let customers request a service time from any device." },
      { title: "Service list", text: "Every service explained so customers know you do it." },
      { title: "Trust badges", text: "Space for certifications, warranties and affiliations you hold." },
      { title: "Tap to call", text: "Your phone number one tap away on every page." },
    ],
    faqs: [BUILD_FAQ, { q: "Can you connect my existing booking software?", a: "Usually, yes. Tell us which tool you use and we'll confirm how it connects during scoping." }],
  },
  {
    slug: "contractors",
    name: "Contractor & Service Provider Websites",
    category: "Websites",
    headline: "Show your work. Win the estimate.",
    intro: "A site that shows off finished projects, makes your service areas obvious and turns visitors into estimate requests.",
    included: ["Project gallery", "Service areas page", "Free estimate form", "Services overview", "Licensing and insurance section", "Click-to-call"],
    whoFor: ["Builders and remodelers", "Roofers, plumbers, electricians", "Landscapers and cleaners", "Any trade or home service"],
    pageTypes: ["Home", "Services", "Gallery", "Service Areas", "Free Estimate", "Contact"],
    features: [
      { title: "Gallery", text: "Before-and-after and finished-project photos, organized by service." },
      { title: "Service areas", text: "The cities and neighborhoods you cover, clearly listed." },
      { title: "Estimate form", text: "Collect job details and photos so you can quote faster." },
      { title: "Mobile-first", text: "Built for homeowners searching from their phones." },
    ],
    faqs: [BUILD_FAQ, { q: "Can customers upload photos with their estimate request?", a: "Yes, the estimate form can include an optional photo upload." }],
  },
  {
    slug: "personal-brand",
    name: "Personal Brand Websites",
    category: "Websites",
    headline: "One place for your name, your story and your offers.",
    intro: "For coaches, creators, consultants and professionals who need a credible home online that they control.",
    included: ["Bio and story page", "Offers or services page", "Booking or contact link", "Media and press section", "Newsletter signup", "Social links"],
    whoFor: ["Coaches and consultants", "Creators and speakers", "Real estate agents", "Freelancers"],
    pageTypes: ["Home", "About", "Work With Me", "Speaking", "Media", "Contact"],
    features: [
      { title: "Your story", text: "A bio that explains who you are and who you help." },
      { title: "Offers", text: "Packages and services presented clearly." },
      { title: "Booking", text: "Link to your calendar so people can book you directly." },
      { title: "Newsletter", text: "Grow an audience you own, not just followers." },
    ],
    faqs: [BUILD_FAQ, { q: "Can you use my existing domain?", a: "Yes. We connect your existing domain at launch, or help you choose one." }],
  },
  {
    slug: "ecommerce",
    name: "E-commerce Websites",
    category: "Websites",
    headline: "Sell online with a store that's simple to shop.",
    intro: "Product pages, a smooth cart and secure checkout, designed so customers can find what they want and buy it from their phone.",
    included: ["Product catalog and categories", "Product detail pages", "Cart", "Secure checkout", "Order notification emails", "Shipping and returns pages"],
    whoFor: ["Retail shops going online", "Makers and small brands", "Businesses selling a few products", "Restaurants selling merch or gift cards"],
    pageTypes: ["Shop", "Product", "Cart", "Checkout", "Shipping & Returns", "Contact"],
    features: [
      { title: "Products", text: "Clean product pages with photos, options and descriptions." },
      { title: "Cart", text: "A simple cart that keeps customers moving toward checkout." },
      { title: "Secure checkout", text: "Checkout through a trusted, secure payment provider." },
      { title: "Mobile shopping", text: "Designed for thumbs first, then large screens." },
    ],
    faqs: [{ q: "How much does an online store cost?", a: "E-commerce scope varies with your catalog and payment setup. We confirm the price in writing before any work begins." }, { q: "Which payment providers do you support?", a: "We'll recommend a secure provider that fits your products during scoping." }],
  },
  {
    slug: "portfolio",
    name: "Portfolio Websites",
    category: "Websites",
    headline: "Let the work speak — loudly.",
    intro: "A portfolio that puts your best projects first, loads fast with large images, and makes it easy to hire you.",
    included: ["Project grid and filters", "Project detail pages", "About and process", "Contact or inquiry form", "Image optimization", "Resume or services page"],
    whoFor: ["Photographers and videographers", "Designers and architects", "Artists and makers", "Agencies and studios"],
    pageTypes: ["Home", "Work", "Project", "About", "Services", "Contact"],
    features: [
      { title: "Big visuals", text: "Large, sharp imagery that still loads quickly." },
      { title: "Filters", text: "Let visitors browse by project type." },
      { title: "Case pages", text: "Tell the story behind each project." },
      { title: "Inquiry form", text: "Collect project details from potential clients." },
    ],
    faqs: [BUILD_FAQ, { q: "Can I add new projects later?", a: "Yes. Send new projects through your client dashboard and we'll add them." }],
  },
  {
    slug: "landing-pages",
    name: "Landing Pages",
    category: "Websites",
    headline: "One page. One goal. No distractions.",
    intro: "Focused pages for a campaign, launch or offer, built around a single call to action.",
    included: ["Single-goal page structure", "Lead capture form", "Offer and benefits sections", "FAQ section", "Click tracking on CTAs", "Mobile-first build"],
    whoFor: ["Ad campaigns", "Product or service launches", "Events and promotions", "Lead magnets"],
    pageTypes: ["Offer page", "Thank-you page", "Event page", "Waitlist page"],
    features: [
      { title: "Focused layout", text: "Everything on the page leads to one action." },
      { title: "Tracked CTAs", text: "Clicks are tracked so you can see what's working." },
      { title: "Fast load", text: "Lightweight pages for paid traffic." },
      { title: "Lead capture", text: "Forms that send leads straight to your inbox." },
    ],
    faqs: [BUILD_FAQ, { q: "Can a landing page connect to my CRM?", a: "Often, yes. Tell us which tool you use and we'll confirm during scoping." }],
  },
  {
    slug: "local-seo",
    name: "Local SEO",
    category: "Growth",
    headline: "Show up when nearby customers search.",
    intro: "Local search foundations for your website and Google Business Profile, so people in your area can find and contact you.",
    included: ["On-page local SEO", "Google Business Profile setup", "Service-area and location pages", "Consistent business details", "Schema markup", "Search Console setup"],
    whoFor: ["Businesses serving a local area", "Shops and restaurants", "Trades and home services", "New businesses getting started"],
    pageTypes: ["Location pages", "Service-area pages", "Service pages", "Contact & directions"],
    features: [
      { id: "google-business-profile", title: "Google Business Profile setup", text: "Claim, verify and complete your profile with hours, services and photos." },
      { title: "Location pages", text: "Pages for the areas you serve, written for people first." },
      { title: "Structured data", text: "Schema markup that helps search engines understand your business." },
      { title: "Consistent details", text: "Name, phone and hours consistent across your site and profile." },
    ],
    faqs: [{ q: "Can you guarantee a #1 ranking?", a: "No one honestly can. We set up strong local foundations and keep them consistent." }, { q: "How is Local SEO priced?", a: "Pricing is confirmed with you in writing before any work begins." }],
  },
  {
    slug: "redesign",
    name: "Website Redesign",
    category: "Growth",
    headline: "Keep what works. Rebuild everything else.",
    intro: "We review your current site, keep the content and search value worth keeping, and rebuild it with a modern design that converts.",
    included: ["Review of your current site", "New design and structure", "Content migration", "Redirects from old pages", "Conversion improvements", "Copy refresh"],
    whoFor: ["Sites that look outdated", "Sites that don't work on phones", "Sites that don't generate calls", "DIY sites you've outgrown"],
    pageTypes: ["Home", "Services", "About", "Contact", "Any existing pages worth keeping"],
    features: [
      { id: "conversion-optimization", title: "Conversion optimization", text: "Clearer calls to action, shorter forms and better page structure." },
      { id: "copywriting", title: "Copywriting & content", text: "Clear, plain-language copy that explains what you do." },
      { title: "Redirects", text: "Old links point to new pages so visitors don't hit dead ends." },
      { title: "Modern build", text: "Fast, mobile-first and easy to maintain." },
    ],
    faqs: [BUILD_FAQ, { q: "Will I lose my existing content?", a: "No. We migrate the content worth keeping and redirect old pages." }],
  },
  {
    slug: "hosting-maintenance",
    name: "Hosting & Maintenance",
    category: "Ongoing",
    headline: "We keep your site fast, secure and up to date.",
    intro: "Hosting & Maintenance is $75/month, month-to-month. We handle hosting, updates, backups and small edits so you can run your business.",
    included: ["Secure hosting with SSL", "Software and security updates", "Regular backups", "Uptime monitoring", "Small content edits on request", "Client dashboard access"],
    whoFor: ["Every NEXORA build", "Owners who don't want to manage tech", "Sites that need regular updates", "Businesses that want a direct line to the builders"],
    pageTypes: ["Applies to every page on your site"],
    features: [
      { id: "care-plans", title: "Website care plans", text: "Ongoing upkeep and small edits handled through your dashboard." },
      { id: "analytics", title: "Analytics & reporting", text: "Set up analytics so you can see where visitors come from." },
      { id: "integrations", title: "Integrations & automations", text: "Connect booking, CRM, email and payment tools you already use." },
      { title: "Cancel anytime", text: "No long-term contract. See the Cancellation Policy for details." },
    ],
    faqs: [{ q: "How much is Hosting & Maintenance?", a: "$75/month, billed monthly. Cancel anytime." }, { q: "What counts as a small edit?", a: "Text, photo, hours and menu updates. Larger changes are quoted first." }],
  },
];

export function getService(slug?: string) {
  return SERVICES.find((s) => s.slug === slug);
}
